import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";

import React, { useState, useEffect } from "react";
import { useDataProvider } from "react-admin";
import { TabbedAnalyticsDashboard } from "./mm/components/TabbedAnalyticsDashboard";
import { DashboardAiProvider } from "./mm/contexts/DashboardAiContext";
import { 
  AgenticFeatureRegistry, 
  initializeAnalyticsDashboardProvider 
} from '../ai/chat';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const Analytics = () => {
  const dataProvider = useDataProvider();
  const [dashboards, setDashboards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Initialize analytics dashboard feature context when component mounts
  // Initialize the analytics dashboard provider (registers event listeners)
  useEffect(() => {
    initializeAnalyticsDashboardProvider();
  }, []);
  
  // Note: Feature context is now managed by route-based context in App.tsx
  // This component only manages focusKey for the active dashboard

  // Fetch all analytics dashboards and trigger preloading
  useEffect(() => {
    const fetchDashboards = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await dataProvider.getList('analytics-dashboards', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { published: true }
        });

        // Filter to only dashboards with valid configurations
        const validDashboards = data.filter(
          (dashboard: any) => dashboard.currentConfig?.config
        );

        setDashboards(validDashboards);

        if (validDashboards.length === 0) {
          setError('No published analytics dashboards found. Please create and publish dashboards in the admin section.');
        }
      } catch (err: any) {
        console.error('Error fetching analytics dashboards:', err);
        setError(err.message || 'Failed to load analytics dashboards');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboards();
  }, [dataProvider]);

  // Set focus when active dashboard changes
  useEffect(() => {
    if (dashboards.length > 0 && dashboards[activeTabIndex]) {
      const dashboard = dashboards[activeTabIndex];
      const dashboardId = String(dashboard.id || dashboard['analytics-dashboardsId']);
      console.log(`🎯 [Analytics View] Setting focus to dashboard: ${dashboardId}`);
      AgenticFeatureRegistry.setFocusKey(dashboardId);
    }
  }, [activeTabIndex, dashboards]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTabIndex(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={48} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Analytics Dashboards...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: "100%", padding: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Please ensure analytics dashboards are configured in the admin section.
        </Typography>
      </Box>
    );
  }

  if (dashboards.length === 0) {
    return (
      <Box sx={{ width: "100%", padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          Analytics Dashboards
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          No published analytics dashboards available yet. Please create and publish dashboards in the
          admin section to view them here.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Dashboard Selection Tabs */}
      <Paper
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          borderRadius: 0,
          boxShadow: 2,
        }}
      >
        <Tabs
          value={activeTabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            backgroundColor: "grey.900",
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTabs-indicator": {
              backgroundColor: "primary.main",
              height: 3,
            },
            "& .MuiTab-root": {
              color: "grey.400",
              fontWeight: 500,
              fontSize: "0.9rem",
              textTransform: "none",
              minHeight: 56,
              px: 3,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "grey.800",
                color: "grey.200",
              },
              "&.Mui-selected": {
                color: "primary.main",
                fontWeight: 600,
              },
            },
          }}
        >
          {dashboards.map((dashboard) => (
            <Tab
              key={dashboard.id}
              label={dashboard.name || dashboard.key}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Dashboard Content */}
      {dashboards.map((dashboard, index) => (
        <TabPanel key={dashboard.id} value={activeTabIndex} index={index}>
          <DashboardAiProvider applicationKey={dashboard.key}>
            <TabbedAnalyticsDashboard
              config={dashboard.currentConfig.config}
              dashboardKey={dashboard.key}
              enableLegacyFallback={false}
              showHeader={false}
              initialUseLiveData={true}
              onTabChange={(tabId) => {
                console.log(`Dashboard ${dashboard.key} tab changed to:`, tabId);
              }}
            />
          </DashboardAiProvider>
        </TabPanel>
      ))}
    </Box>
  );
};

export default Analytics;
